import {
  getCustomerCompanyRetrieveQueryKey,
  getCustomerFundingNeedsListQueryKey,
  getCustomerFundingNeedsRetrieveQueryKey,
  useCustomerFundingNeedsCreate,
} from "@boundless/bl-api-client/customer/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import {
  createFileRoute,
  redirect,
  retainSearchParams,
} from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { FundingNeedsForm } from "@/components/funding-needs/FundingNeedsForm"
import { Card, CardTitle } from "@/components/ui/card"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { ApplicationFlowContext } from "@/lib/flows/application"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import { fundingNeedsFormSchema } from "@/lib/schemas"

export const Route = createFileRoute(
  "/_dashboard/_application-flow/funding-requests/create",
)({
  validateSearch: z.object({
    from: z.string().optional(),
  }),
  component: FundingRequestCreate,
  search: {
    middlewares: [retainSearchParams(true)],
  },
  beforeLoad: ({ context: { store } }) => {
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/home" })
    }
    return { companyUuid }
  },
})

function FundingRequestCreate() {
  const { companyUuid } = Route.useRouteContext()
  const queryClient = useQueryClient()

  const flowActorRef = ApplicationFlowContext.useActorRef()
  const goNext = () => flowActorRef.send({ type: "next" })
  const goBack = () => flowActorRef.send({ type: "back" })

  const form = useForm<z.infer<typeof fundingNeedsFormSchema>>({
    resolver: zodResolver(fundingNeedsFormSchema),
  })
  const companyQueryKey = getCustomerCompanyRetrieveQueryKey(companyUuid)
  const fundingNeedsListQueryKey =
    getCustomerFundingNeedsListQueryKey(companyUuid)

  const { mutate: createFundingNeeds } = useCustomerFundingNeedsCreate({
    mutation: {
      onSuccess: (fundingNeeds) => {
        queryClient.invalidateQueries({ queryKey: companyQueryKey })
        queryClient.invalidateQueries({ queryKey: fundingNeedsListQueryKey })
        queryClient.setQueryData(
          getCustomerFundingNeedsRetrieveQueryKey(
            companyUuid,
            fundingNeeds.uuid,
          ),
          fundingNeeds,
        )

        flowActorRef.send({
          type: "setFundingNeeds",
          fundingNeeds,
        })
        goNext()
      },
      onError: (error: ErrorType<unknown>) => {
        applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
      },
    },
  })

  const handleSubmit = (data: z.infer<typeof fundingNeedsFormSchema>) => {
    createFundingNeeds({
      companyUuid: companyUuid,
      data: {
        ...data,
      },
    })
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Card className="col-span-1 p-6 lg:col-span-2">
        <CardTitle>Funding Need</CardTitle>
        <Form {...form}>
          <form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
            <FundingNeedsForm />
            <div className="mt-8 flex flex-col gap-2 md:flex-row md:justify-end">
              <ButtonBack onClick={goBack} />
              <ButtonNext type="submit" />
            </div>
          </form>
        </Form>
      </Card>
    </div>
  )
}
